/**
 * Chat Message Zod Schema
 *
 * Validates the shape returned by GetMessages (GetMessagesResult from the backend).
 * Mirrors the Prisma select in GetMessagesRow without constraining the JSON content field —
 * content is accepted as-is via passthrough so that all existing message variants
 * (text, tool, plan, workflow, file, source) continue to work without breakage.
 */

import { z } from "zod";

// ── Nested relation schemas ────────────────────────────────────────────────────

const chatMessageAgentSchema = z
  .object({
    first_name: z.string().nullable(),
    last_name: z.string().nullable(),
    profile_picture: z.string().nullable()
  })
  .nullable();

const chatMessageUserSchema = z
  .object({
    id: z.string(),
    firstName: z.string().nullable(),
    lastName: z.string().nullable()
  })
  .nullable();

const chatMessageFileSchema = z.object({
  name: z.string().nullable(),
  textContent: z.string().nullable().optional()
});

const chatMessageSatisfactionScoreSchema = z
  .object({
    score: z.number()
  })
  .nullable();

// ── Main schema ────────────────────────────────────────────────────────────────

/**
 * Zod schema for a message record returned by the GetMessages endpoint.
 *
 * Content is accepted as any JSON value (z.unknown()) because message content
 * varies by type (text, tool output, plan, workflow, file, etc.).
 * Use the domain-level message content schemas for further narrowing when needed.
 */
export const chatMessageSchema = z.object({
  id: z.string(),
  type: z.string().nullable(),
  content: z.unknown(),
  toolCalls: z.array(z.unknown()),
  toolCallId: z.string().nullable(),
  toolType: z.string().nullable(),
  status: z.string().nullable(),
  cancelled: z.boolean().nullable(),
  statusInfos: z.unknown().nullable(),
  agentId: z.string().nullable(),
  agent: chatMessageAgentSchema.optional(),
  chatId: z.string(),
  files: z.array(chatMessageFileSchema).optional(),
  userId: z.string().nullable(),
  user: chatMessageUserSchema.optional(),
  createdAt: z.union([z.string(), z.date()]),
  updatedAt: z.union([z.string(), z.date()]).nullable(),
  timezoneOffsetHours: z.number().nullable(),
  reasoning: z.string().nullable(),
  reasoningDurationMs: z.number().nullable(),
  satisfactionScore: chatMessageSatisfactionScoreSchema.optional(),
  metaConversationId: z.string().nullable()
});

export type ChatMessageSchema = z.infer<typeof chatMessageSchema>;
